import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ResultCard } from '../components/shared/ResultCard';
import { Shield, CheckCircle2, AlertTriangle, FileText, Database, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { compliance, soc2Evidence, piiLineage } from '../data/mockData';
import { useToast } from '../context/ToastContext';

export const Compliance = () => {
  const location = useLocation();
  const { addToast } = useToast();
  const [activeView, setActiveView] = useState('dashboard');

  const handleAction = (action) => {
    addToast(`Demo action: ${action}`, 'info');
  };

  const renderDashboard = () => (
    <>
      {/* Compliance Posture */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-black mb-4">Compliance Posture</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {compliance.frameworks.map((fw, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-black">{fw.name}</h3>
                <span className={`text-2xl font-bold ${
                  fw.score >= 90 ? 'text-primary' : fw.score >= 80 ? 'text-slate-600' : 'text-slate-900'
                }`}>{fw.score}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                <div
                  className={`h-2 rounded-full transition-all ${
                    fw.score >= 90 ? 'bg-primary' : fw.score >= 80 ? 'bg-slate-400' : 'bg-slate-900'
                  }`}
                  style={{ width: `${fw.score}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">{fw.issues} {fw.status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Critical Findings */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-black mb-4">Critical Findings</h2>
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {compliance.findings.map((finding, idx) => (
            <div key={idx} className="px-5 py-4">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-4.5 h-4.5 text-slate-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">{finding.framework}</span>
                    <span className="font-medium text-black">{finding.description}</span>
                  </div>
                  {finding.items && <p className="text-sm text-gray-500">{finding.items}</p>}
                  {finding.added && <p className="text-sm text-gray-500">Added: {finding.added} | {finding.status}</p>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAction('Investigate')}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Investigate
                  </button>
                  <button
                    onClick={() => handleAction('Create ticket')}
                    className="px-3 py-1.5 text-sm text-white bg-[#4a70ff] rounded-lg hover:bg-[#3a5fe6] transition-colors"
                  >
                    Create ticket
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 gap-4">
        <button
          onClick={() => setActiveView('evidence')}
          className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-[#4a70ff]/30 hover:shadow-lg hover:shadow-[#4a70ff]/5 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#4a70ff]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#4a70ff]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-black group-hover:text-[#4a70ff] transition-colors">Evidence Generator</h3>
              <p className="text-sm text-gray-500">Generate compliance evidence packages</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#4a70ff] transition-colors" />
          </div>
        </button>

        <button
          onClick={() => setActiveView('lineage')}
          className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-[#4a70ff]/30 hover:shadow-lg hover:shadow-[#4a70ff]/5 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#4a70ff]/10 flex items-center justify-center">
              <Database className="w-5 h-5 text-[#4a70ff]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-black group-hover:text-[#4a70ff] transition-colors">Data Lineage</h3>
              <p className="text-sm text-gray-500">Track PII flow across systems</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#4a70ff] transition-colors" />
          </div>
        </button>
      </div>
    </>
  );

  const renderEvidence = () => (
    <>
      <button
        onClick={() => setActiveView('dashboard')}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Compliance Dashboard
      </button>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-black">{soc2Evidence.title}</h2>
              <p className="text-sm text-gray-500 mt-1">Generated: {soc2Evidence.generated} | Period: {soc2Evidence.period}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleAction('Download PDF')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#4a70ff] rounded-lg hover:bg-[#3a5fe6] transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => handleAction('Download CSV')}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">{soc2Evidence.control}</h3>
          
          <div className="space-y-6">
            {soc2Evidence.evidence.map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-black">{item.title}</p>
                  {item.coverage && <p className="text-sm text-gray-600">Coverage: {item.coverage}</p>}
                  {item.exceptions !== undefined && <p className="text-sm text-gray-600">Exceptions: {item.exceptions}</p>}
                  {item.lastReview && <p className="text-sm text-gray-600">Last review: {item.lastReview}</p>}
                  {item.revocations && <p className="text-sm text-gray-600">Revocations: {item.revocations}</p>}
                  {item.details && (
                    <ul className="mt-1 space-y-0.5">
                      {item.details.map((d, i) => (
                        <li key={i} className="text-sm text-gray-600">{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={() => handleAction('Schedule recurring')}
            className="text-sm text-[#4a70ff] hover:underline"
          >
            Schedule recurring evidence generation
          </button>
        </div>
      </div>
    </>
  );

  const renderLineage = () => (
    <>
      <button
        onClick={() => setActiveView('dashboard')}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Compliance Dashboard
      </button>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-black">PII DATA LINEAGE</h2>
        <p className="text-sm text-gray-500 mt-1">Where does customer PII flow?</p>
      </div>

      <div className="space-y-6">
        {/* Collection Points */}
        <ResultCard
          title="COLLECTION POINTS"
          sections={[{
            content: (
              <div className="flex flex-wrap gap-2">
                {piiLineage.collectionPoints.map((point, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm">{point}</span>
                ))}
              </div>
            )
          }]}
        />

        {/* Flow Diagram */}
        <div className="flex items-center justify-center py-4">
          <ArrowRight className="w-6 h-6 text-gray-300 rotate-90" />
        </div>

        {/* Primary Storage */}
        <ResultCard
          title="PRIMARY STORAGE"
          sections={[{
            content: (
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-[#4a70ff]" />
                <span className="text-sm font-medium">{piiLineage.primaryStorage}</span>
              </div>
            )
          }]}
        />

        {/* Flow Diagram */}
        <div className="flex items-center justify-center py-4">
          <ArrowRight className="w-6 h-6 text-gray-300 rotate-90" />
        </div>

        {/* Processing */}
        <div className="grid md:grid-cols-3 gap-4">
          {piiLineage.processing.map((proc, idx) => (
            <ResultCard
              key={idx}
              title={proc.category}
              sections={[{
                content: (
                  <ul className="space-y-1">
                    {proc.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600">{item}</li>
                    ))}
                  </ul>
                )
              }]}
            />
          ))}
        </div>

        {/* Retention */}
        <ResultCard
          title="RETENTION"
          sections={[{
            content: (
              <div className="space-y-2">
                {piiLineage.retention.map((ret, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="font-medium">{ret.system}</span>
                    <span className="text-gray-500">{ret.policy}</span>
                  </div>
                ))}
              </div>
            )
          }]}
        />

        {/* Findings */}
        <div className="bg-slate-100 border border-slate-300 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-5 h-5 text-slate-900 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-1">FINDINGS</h3>
              {piiLineage.findings.map((finding, idx) => (
                <p key={idx} className="text-sm text-slate-700">{finding.issue} ({finding.severity})</p>
              ))}
            </div>
            <button
              onClick={() => handleAction('Create remediation ticket')}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
            >
              Create remediation ticket
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-black mb-1">Compliance Intelligence</h1>
          <p className="text-gray-500">Continuous compliance posture, audit evidence, and regulatory mapping</p>
        </div>

        {activeView === 'dashboard' && renderDashboard()}
        {activeView === 'evidence' && renderEvidence()}
        {activeView === 'lineage' && renderLineage()}
      </div>
    </div>
  );
};